import { createClient } from '@supabase/supabase-js';

// Secure environment variable handling with strict validation
const getEnvVar = (key: string): string => {
  const value = import.meta.env[key];
  if (!value || value.trim() === '') {
    const errorMsg = `CRITICAL: Environment variable ${key} is not set or is empty. Application cannot start without proper configuration.`;
    // Only log in development
    if (import.meta.env.DEV) {
      console.error(errorMsg);
    }
    throw new Error(errorMsg);
  }
  return value.trim();
};

// Validate URL format and security requirements
const validateSupabaseUrl = (url: string): void => {
  try {
    const parsedUrl = new URL(url);
    
    // Must use HTTPS
    if (parsedUrl.protocol !== 'https:') {
      throw new Error('Supabase URL must use HTTPS protocol');
    }
    
    // Must be a valid Supabase domain
    if (!parsedUrl.hostname.endsWith('.supabase.co')) {
      throw new Error('Invalid Supabase domain. Expected *.supabase.co');
    }
    
    // Must have a project ID (subdomain)
    const subdomain = parsedUrl.hostname.split('.')[0];
    if (!subdomain || subdomain.length < 10) {
      throw new Error('Invalid Supabase project ID in URL');
    }
  } catch (error) {
    const errorMsg = `Invalid VITE_SUPABASE_URL: ${error instanceof Error ? error.message : 'Unknown error'}`;
    if (import.meta.env.DEV) {
      console.error(errorMsg);
    }
    throw new Error(errorMsg);
  }
};

// Validate anon key format
const validateAnonKey = (key: string): void => {
  // Supabase anon keys are JWT tokens, should start with 'eyJ'
  if (!key.startsWith('eyJ')) {
    const errorMsg = 'Invalid VITE_SUPABASE_ANON_KEY format. Expected a valid JWT token.';
    if (import.meta.env.DEV) {
      console.error(errorMsg);
    }
    throw new Error(errorMsg);
  }
  
  // Basic length check (JWT tokens are typically quite long)
  if (key.length < 100) {
    const errorMsg = 'VITE_SUPABASE_ANON_KEY appears to be too short. Expected a valid JWT token.';
    if (import.meta.env.DEV) {
      console.error(errorMsg);
    }
    throw new Error(errorMsg);
  }
};

// Get and validate environment variables
let supabaseUrl: string;
let supabaseAnonKey: string;

try {
  supabaseUrl = getEnvVar('VITE_SUPABASE_URL');
  supabaseAnonKey = getEnvVar('VITE_SUPABASE_ANON_KEY');
  
  // Validate the values
  validateSupabaseUrl(supabaseUrl);
  validateAnonKey(supabaseAnonKey);
  
  // Only log in development
  if (import.meta.env.DEV) {
    console.log('✅ Supabase configuration validated successfully');
  }
} catch (error) {
  if (import.meta.env.DEV) {
    console.error('❌ Supabase configuration validation failed:', error);
  }
  throw error;
}

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: window.localStorage,
    // Use implicit flow for email-based auth (password reset, email confirmation)
    // PKCE is for OAuth providers, not email links
    flowType: 'implicit',
    debug: import.meta.env.DEV
  }
});
